import { oids } from "@/configs/Settings";
import { fhirClients } from "src-next/shared/api/fhirClient";

export interface WhatsAppEndpointContactsResult {
  endpointId: string;
  contacts: fhir5.ContactPoint[];
}

function extractPhoneContactsFromEndpoint(
  endpoint: fhir5.Endpoint,
): fhir5.ContactPoint[] {
  const contactPoints = endpoint.contact ?? [];

  // Só mostramos entradas do tipo "phone".
  const phoneContacts = contactPoints.filter((c) => c.system === "phone");

  // Mantém rank em ordem previsível.
  return phoneContacts
    .map((contactPoint, index) => ({
      ...contactPoint,
      // Alguns servidores podem omitir rank. Garantimos ordenação estável.
      rank:
        typeof contactPoint.rank === "number"
          ? contactPoint.rank
          : // rank começa em 1
            index + 1,
    }))
    .sort((a, b) => {
      const rankA = typeof a.rank === "number" ? a.rank : 1;
      const rankB = typeof b.rank === "number" ? b.rank : 1;
      return rankA - rankB;
    });
}

function normalizeOrganizationId(organizationId?: string): string | null {
  if (!organizationId?.trim()) return null;
  return organizationId.trim();
}

function extractEndpointEntries(bundle: fhir5.Bundle): fhir5.Endpoint[] {
  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Endpoint =>
        resource !== undefined && resource.resourceType === "Endpoint",
    );
}

function hasMatchingManagingOrganization(
  endpoint: fhir5.Endpoint,
  organizationId: string,
): boolean {
  const managingOrganizationReference =
    endpoint.managingOrganization?.reference ?? "";
  return managingOrganizationReference.endsWith(`/${organizationId}`);
}

async function findActiveEndpointByOrganizationId(
  organizationId: string,
): Promise<(fhir5.Endpoint & { id: string }) | null> {
  const bundle = await fhirClients.admin.search<fhir5.Endpoint>("Endpoint", {
    organization: `${oids.cnpj}-${organizationId}`,
    status: "active",
    _count: "50",
  });
  const endpointCandidates = extractEndpointEntries(bundle);

  const activeCandidate = endpointCandidates.find(
    (candidate) => candidate.status === "active" && Boolean(candidate.id),
  );
  if (!activeCandidate?.id) return null;
  return activeCandidate as fhir5.Endpoint & { id: string };
}

async function listContactsByOrganizationId(
  organizationId?: string,
): Promise<WhatsAppEndpointContactsResult> {
  const normalizedOrganizationId = normalizeOrganizationId(organizationId);
  if (!normalizedOrganizationId) {
    throw new Error("organizationId é obrigatório para buscar Endpoint.");
  }

  const endpoint = await findActiveEndpointByOrganizationId(
    normalizedOrganizationId,
  );
  if (!endpoint) {
    throw new Error("Nenhum Endpoint ativo encontrado para esta organização.");
  }

  return {
    endpointId: endpoint.id,
    contacts: extractPhoneContactsFromEndpoint(endpoint),
  };
}

async function reorderContactsByEndpointId(input: {
  endpointId: string;
  contacts: fhir5.ContactPoint[];
}): Promise<void> {
  // Patch parcial: atualiza apenas o array `contact` com ranks e ordem.
  const normalizedContacts = input.contacts.map((contactPoint, index) => ({
    ...contactPoint,
    rank: index + 1,
  }));
  await fhirClients.admin.patch<fhir5.Endpoint>("Endpoint", input.endpointId, [
    { op: "replace", path: "/contact", value: normalizedContacts },
  ]);
}

export const whatsappEndpointService = {
  listContactsByOrganizationId,
  reorderContactsByEndpointId,
};
